/**
 * LoginPage Component
 *
 * Dedicated login frame page with AttuneWell branding.
 * Auto-triggers Auth0 popup on mount so the login window opens immediately.
 * Gracefully handles the closed-popup case with a friendly re-prompt.
 */

import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { HeartHandshake, Mail, UserPlus, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { useAuth } from "../hooks/useAuth";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { useOnboarding } from "../hooks/useOnboarding";
import { AuthTransition } from "../components/AuthTransition";

type LoginState = "idle" | "authenticating" | "closed" | "error";

export function LoginPage() {
  const { login, loginWithRedirect } = useAuth();
  const { isAuthenticated } = useAuthStatus();
  const { isOnboardingComplete, isLoading: onboardingLoading } = useOnboarding({
    enabled: isAuthenticated,
  });
  const navigate = useNavigate();

  const [loginState, setLoginState] = useState<LoginState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    if (onboardingLoading) return;

    if (isOnboardingComplete) {
      navigate("/dashboard", { replace: true });
    } else {
      navigate("/onboarding", { replace: true });
    }
  }, [isAuthenticated, isOnboardingComplete, onboardingLoading, navigate]);

  // Auto-trigger Auth0 login on mount
  useEffect(() => {
    if (isAuthenticated) return;

    const triggerLogin = async () => {
      setLoginState("authenticating");
      setErrorMessage(null);
      try {
        await login();
        // If we get here without error, the popup was closed without completing
        setLoginState("closed");
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        if (/popup closed/i.test(message)) {
          setLoginState("closed");
          return;
        }
        // Any other error — fall back to redirect
        setLoginState("error");
        setErrorMessage(message || "Something went wrong. Please try again.");
      }
    };

    triggerLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTryAgain = async () => {
    setLoginState("authenticating");
    try {
      await login();
      setLoginState("closed");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      if (/popup closed/i.test(message)) {
        setLoginState("closed");
        return;
      }
      setLoginState("error");
      setErrorMessage(message || "Something went wrong. Please try again.");
    }
  };

  const handleContinueWithEmail = async () => {
    setLoginState("authenticating");
    try {
      await loginWithRedirect({
        authorizationParams: { screen_hint: "login" },
      });
    } catch {
      setLoginState("error");
      setErrorMessage("Could not open login page. Please try again.");
    }
  };

  const handleCreateAccount = async () => {
    setLoginState("authenticating");
    try {
      await loginWithRedirect({
        authorizationParams: { screen_hint: "signup" },
      });
    } catch {
      setLoginState("error");
      setErrorMessage("Could not open signup page. Please try again.");
    }
  };

  if (loginState === "authenticating") {
    return (
      <div className="min-h-screen flex flex-col">
        {/* Minimal header */}
        <header className="border-b border-border bg-background">
          <div className="container mx-auto px-4 h-16 flex items-center">
            <div className="flex items-center gap-2">
              <HeartHandshake className="w-6 h-6 text-primary" />
              <span className="text-xl font-bold text-foreground">
                AttuneWell
              </span>
            </div>
          </div>
        </header>
        <AuthTransition message="Signing you in..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Minimal header */}
      <header className="border-b border-border bg-background">
        <div className="container mx-auto px-4 h-16 flex items-center">
          <div className="flex items-center gap-2">
            <HeartHandshake className="w-6 h-6 text-primary" />
            <span className="text-xl font-bold text-foreground">
              AttuneWell
            </span>
          </div>
        </div>
      </header>

      {/* Centered content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md space-y-8">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center border-2 border-primary/20">
              <HeartHandshake className="w-8 h-8 text-primary" />
            </div>
          </div>

          {/* Closed popup re-prompt */}
          {loginState === "closed" && (
            <Card className="border-primary/30 bg-primary/5">
              <div className="pt-6 pb-6 text-center space-y-4">
                <AlertCircle className="w-8 h-8 text-primary mx-auto" />
                <div>
                  <p className="font-semibold text-foreground">
                    Login window closed
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    It looks like you closed the login window before signing in.
                  </p>
                </div>
                <Button onClick={handleTryAgain} className="w-full">
                  Try Again
                </Button>
              </div>
            </Card>
          )}

          {/* Generic error */}
          {loginState === "error" && (
            <Card className="border-destructive/30 bg-destructive/5">
              <div className="pt-6 pb-6 text-center space-y-4">
                <AlertCircle className="w-8 h-8 text-destructive mx-auto" />
                <div>
                  <p className="font-semibold text-foreground">
                    Something went wrong
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {errorMessage || "Please try again."}
                  </p>
                </div>
                <Button onClick={handleTryAgain} className="w-full">
                  Try Again
                </Button>
              </div>
            </Card>
          )}

          {/* Main login card */}
          {(loginState === "idle" ||
            loginState === "closed" ||
            loginState === "error") && (
            <div className="text-center space-y-6">
              <div className="space-y-2">
                <h1 className="text-3xl font-bold text-foreground">
                  Welcome back
                </h1>
                <p className="text-muted-foreground">
                  Sign in to continue your therapy journey
                </p>
              </div>

              <div className="space-y-3">
                {/* Continue with Google — triggers the popup login */}
                <Button
                  onClick={handleTryAgain}
                  className="w-full gap-2"
                  size="lg"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </Button>

                {/* Continue with Email */}
                <Button
                  variant="secondary"
                  onClick={handleContinueWithEmail}
                  className="w-full gap-2"
                  size="lg"
                >
                  <Mail className="w-5 h-5" />
                  Continue with Email
                </Button>
              </div>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-background px-4 text-sm text-muted-foreground">
                    or
                  </span>
                </div>
              </div>

              {/* Create account */}
              <Button
                variant="ghost"
                onClick={handleCreateAccount}
                className="w-full gap-2"
                size="lg"
              >
                <UserPlus className="w-5 h-5" />
                Create an account
              </Button>

              {/* Legal note */}
              <p className="text-xs text-muted-foreground text-center">
                By continuing, you agree to our{" "}
                <Link to="/legal" className="underline hover:text-foreground">
                  Terms
                </Link>{" "}
                and{" "}
                <Link to="/legal" className="underline hover:text-foreground">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
